import { useState } from "react";
import { Sparkles, Loader2, Wand2, Globe, DownloadCloud } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";

const inputCls = "w-full bg-ink-800 border border-ink-600 rounded-lg px-3 py-2 text-sm outline-none focus:border-brand transition-colors text-white";
const TONES = ["COOL + CONVERSATIONAL", "PERSONAL", "EDUCATIONAL", "MOTIVATIONAL", "STRAIGHTFORWARD", "URBAN + MODERN", "PROFESSIONAL", "CUSTOM"];
const TYPES = ["How-to guide", "Opinion / Real talk", "Listicle", "Case study", "Beginner explainer", "Tool review", "Story"];

export default function AiWriter({ onGenerated }) {
  const [cfg, setCfg] = useState({
    topic: "", article_type: "How-to guide", target_reader: "Everyday person curious about online business",
    tone: "COOL + CONVERSATIONAL", custom_tone: "", length: "MEDIUM", keywords: "", notes: "", reference_urls: "", cta_goal: "",
  });
  const [loading, setLoading] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const generate = async () => {
    if (!cfg.topic.trim()) { toast.error("Give the writer a topic first"); return; }
    setLoading(true);
    try {
      const payload = {
        ...cfg,
        keywords: cfg.keywords.split(",").map((s) => s.trim()).filter(Boolean),
        reference_urls: cfg.reference_urls.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.post("/admin/ai/generate", payload);
      toast.success("Draft generated. Review it before publishing.");
      onGenerated(data);
    } catch (e) {
      toast.error(apiErr(e, "AI generation failed. Try again."));
    } finally { setLoading(false); }
  };

  const capture = async () => {
    const urls = cfg.reference_urls.split("\n").map((s) => s.trim()).filter(Boolean);
    if (!urls.length) { toast.error("Add at least one URL to capture from"); return; }
    setCapturing(true);
    try {
      const payload = {
        urls, topic: cfg.topic, article_type: cfg.article_type, target_reader: cfg.target_reader,
        tone: cfg.tone, custom_tone: cfg.custom_tone, length: cfg.length, notes: cfg.notes, cta_goal: cfg.cta_goal,
      };
      const { data } = await api.post("/admin/ai/capture", payload);
      const used = (data._sources_used || []).length;
      const failed = (data._sources_failed || []).length;
      toast.success(`Original draft created from ${used} source${used !== 1 ? "s" : ""}.${failed ? ` ${failed} could not be read.` : ""}`);
      onGenerated(data);
    } catch (e) {
      toast.error(apiErr(e, "Could not capture those URLs. Try a different source."));
    } finally { setCapturing(false); }
  };

  return (
    <div className="rounded-xl border border-brand/30 bg-gradient-to-br from-ink-900 to-ink-950 p-6" data-testid="ai-writer">
      <div className="flex items-center gap-2 mb-1"><Sparkles size={18} className="text-brand" /><span className="label-mono">AI Writer Studio</span></div>
      <h2 className="font-display font-extrabold text-white text-xl mb-1">Generate with AI</h2>
      <p className="text-slate-400 text-sm mb-5">Configure the brief. The draft loads into the editor for you to review, edit, and publish. Nothing publishes automatically.</p>

      <div className="space-y-4">
        <div>
          <label className="text-xs text-slate-400 font-mono-label">Topic *</label>
          <input value={cfg.topic} onChange={(e) => set("topic", e.target.value)} data-testid="ai-topic" className={`${inputCls} mt-1.5`} placeholder="e.g. Is dropshipping still worth trying in 2026?" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-slate-400 font-mono-label">Article type</label>
            <select value={cfg.article_type} onChange={(e) => set("article_type", e.target.value)} className={`${inputCls} mt-1.5`}>{TYPES.map((t) => <option key={t}>{t}</option>)}</select>
          </div>
          <div>
            <label className="text-xs text-slate-400 font-mono-label">Length</label>
            <select value={cfg.length} onChange={(e) => set("length", e.target.value)} className={`${inputCls} mt-1.5`}><option value="SHORT">Short</option><option value="MEDIUM">Medium</option><option value="LONG">Long</option></select>
          </div>
        </div>
        <div>
          <label className="text-xs text-slate-400 font-mono-label">Target reader</label>
          <input value={cfg.target_reader} onChange={(e) => set("target_reader", e.target.value)} className={`${inputCls} mt-1.5`} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-slate-400 font-mono-label">Tone</label>
            <select value={cfg.tone} onChange={(e) => set("tone", e.target.value)} data-testid="ai-tone" className={`${inputCls} mt-1.5`}>{TONES.map((t) => <option key={t}>{t}</option>)}</select>
          </div>
          <div>
            <label className="text-xs text-slate-400 font-mono-label">Keywords (comma separated)</label>
            <input value={cfg.keywords} onChange={(e) => set("keywords", e.target.value)} className={`${inputCls} mt-1.5`} placeholder="dropshipping, low budget" />
          </div>
        </div>
        {cfg.tone === "CUSTOM" && (
          <div>
            <label className="text-xs text-slate-400 font-mono-label">Custom tone instruction</label>
            <input value={cfg.custom_tone} onChange={(e) => set("custom_tone", e.target.value)} className={`${inputCls} mt-1.5`} placeholder="Describe the exact voice you want" />
          </div>
        )}
        <div>
          <label className="text-xs text-slate-400 font-mono-label">CTA goal</label>
          <input value={cfg.cta_goal} onChange={(e) => set("cta_goal", e.target.value)} className={`${inputCls} mt-1.5`} placeholder="e.g. Encourage readers to try Shopify's free trial" />
        </div>
        <div className="rounded-lg border border-brand/30 bg-ink-950/60 p-4">
          <div className="flex items-center gap-2 mb-1"><Globe size={15} className="text-brand" /><span className="text-xs text-brand font-mono-label uppercase tracking-wider">Capture from URL</span></div>
          <p className="text-xs text-slate-400 mb-3">Paste one or more article URLs (one per line). The reader will read them and write a brand new, original piece in the Lucrative Laptop voice. It never copies the source wording. Best results come from blending two or three sources.</p>
          <textarea value={cfg.reference_urls} onChange={(e) => set("reference_urls", e.target.value)} rows={3} data-testid="ai-capture-urls" className={inputCls} placeholder={"https://example.com/article-one\nhttps://example.com/article-two"} />
          <button onClick={capture} disabled={capturing || loading} data-testid="ai-capture" className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-brand text-brand font-bold py-2.5 rounded-lg hover:bg-brand hover:text-brand-dark transition-colors disabled:opacity-60">
            {capturing ? <><Loader2 size={16} className="animate-spin" /> Reading and rewriting...</> : <><DownloadCloud size={16} /> Capture and rewrite</>}
          </button>
          {capturing && <p className="text-xs text-slate-500 text-center mt-2">Fetching the pages and writing an original draft. This can take a minute.</p>}
        </div>
        <div>
          <label className="text-xs text-slate-400 font-mono-label">Your notes / research (optional)</label>
          <textarea value={cfg.notes} onChange={(e) => set("notes", e.target.value)} rows={3} className={`${inputCls} mt-1.5`} placeholder="Paste any points, angles, or facts you want included." />
        </div>

        <div className="flex items-center gap-3 pt-1">
          <span className="flex-1 h-px bg-ink-600" /><span className="text-[10px] text-slate-500 font-mono-label uppercase">or write from scratch</span><span className="flex-1 h-px bg-ink-600" />
        </div>

        <button onClick={generate} disabled={loading || capturing} data-testid="ai-generate" className="w-full inline-flex items-center justify-center gap-2 bg-brand text-brand-dark font-bold py-3 rounded-lg hover:bg-brand-neon transition-colors disabled:opacity-60">
          {loading ? <><Loader2 size={17} className="animate-spin" /> Writing your draft...</> : <><Wand2 size={17} /> Generate draft</>}
        </button>
        {loading && <p className="text-xs text-slate-500 text-center">This can take up to a minute for longer articles.</p>}
      </div>
    </div>
  );
}
